<template>
<!-- eslint-disable max-len -->
<div class="container" style="padding: 0;">
<div class="row no-gutters">
<div
  :class="`game-launcher ${inline ? '' : 'col-sm-8 offset-sm-2 col-md-6 offset-md-3 col-lg-4 offset-lg-4'}`"
  :style="`background-color: ${backgroundColor}; color: ${foregroundColor}; padding: ${padding}; transition: all .15s linear;`"
>
  <h2 :style="`text-align: ${(noCloseButton && !textLeftAligned) ? 'right' : 'left'}; font-size: ${labelSize}; font-weight: 100;`">
    select {{classMode ? classTypeNameLower : 'gamulation'}}
    <a v-if="!noCloseButton" href="" @click.prevent="onCancel" class="btn btn-sm float-right mb-3" style="font-size: 1.5rem;">
      &times;
    </a>
  </h2>

  <form>
    <select-input
      v-if="classMode"
      v-model="classId"
      :validator="$v.classId"
      :options="classes"
      use="_id"
      display="className"
      :required="!noSubmitButton"
      autofocus
      :dropdownForeground="dropdownForeground"
      :dropdownBackground="dropdownBackground"
      :dropdownOptionBackground="dropdownOptionBackground"
      :dropdownOptionForeground="dropdownOptionForeground"
      :borderColor="borderColor"
      @change="onChange"
    >
      {{classTypeName}}
    </select-input>
    <select-input
      v-else
      v-model="gameId"
      :validator="$v.gameId"
      :options="games"
      use="_id"
      display="name"
      :required="!noSubmitButton"
      autofocus
      :dropdownForeground="dropdownForeground"
      :dropdownBackground="dropdownBackground"
      :dropdownOptionBackground="dropdownOptionBackground"
      :dropdownOptionForeground="dropdownOptionForeground"
      :borderColor="borderColor"
      @change="onChange"
    >
      Game
    </select-input>

    <button
      v-if="!noSubmitButton"
      type="submit"
      class="btn"
      @click.prevent.once="onSubmit"
      :disabled="$v.$invalid">
      {{selectionSameAsCurrent ? 'Restart' : `Save And Load Selected ${classMode ? classTypeName : 'Gamulation'}`}}
    </button>
  </form>
</div>
</div>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';

import SelectInput from '../inputs/SelectInput';

export default {
  components: {
    SelectInput
  },
  props: {
    textLeftAligned: { type: Boolean, default: false },
    backgroundColor: { type: String, default: 'rgba(0, 0, 7, 0.76)' },
    foregroundColor: { type: String, default: 'white' },
    labelSize: { type: String, default: '2rem' },
    padding: { type: String, default: '20px' },
    noCloseButton: { type: Boolean, default: false },
    noSubmitButton: { type: Boolean, default: false },
    inline: { type: Boolean, default: false },
    dropdownBackground: { type: String, default: 'white' },
    dropdownForeground: { type: String, default: 'black' },
    dropdownOptionBackground: { type: String, default: 'white' },
    dropdownOptionForeground: { type: String, default: 'black' },
    borderColor: { type: String, default: 'lightgrey' },
  },
  data() {
    return {
      gameId: '',
      games: [],
      classId: '',
      classes: []
    };
  },
  async created() {
    try {
      this.games = ((this.canSelectGame) ? (await this.getGames()) : await this.getAssignedGames());
      if (this.game != null) {
        this.gameId = this.game._id;
      }

      this.classes = await this.getClasses();
      if (this.activeClass != null) {
        this.classId = this.activeClass._id;
      }
    } catch (err) {
      console.error(err);
      const message = 'Error fetching gamulations, please try again';
      this.flashDanger({ message });
      this.onCancel();
    }
  },
  computed: {
    ...mapGetters([
      'activeClass',
      'game',
      'group',
      'canSelectGame',
      'canRestartGame',
      'isStudentOrEmployee',
      'classTypeName',
      'classTypeNameLower',
      'me'
    ]),
    classMode() {
      return this.me.gameSelectionOverride === 'Use Classes' || (this.me.gameSelectionOverride !== 'Use Gamulations' && this.isStudentOrEmployee);
    },
    selectionSameAsCurrent() {
      return this.classMode
        ? (this.classId === this.activeClass._id)
        : (this.gameId === this.game._id);
    },
  },
  methods: {
    ...mapActions([
      'flashDanger',
      'getGames',
      'archiveGroup',
      'getMyGroup',
      'getAssignedGames',
      'getClasses'
    ]),
    onChange() {
      if (this.classMode) {
        const { classId } = this;
        const chosenClass = this.classes.find(c => c._id === classId);
        this.gameId = chosenClass.game;
      }
      if (this.noSubmitButton) {
        this.onSubmit();
      }
    },
    async onSubmit() {
      const { gameId, classId } = this;
      this.$emit('input', { gameId });
      if (!(this.canSelectGame || this.canRestartGame) || !gameId) return;

      try {
        if (this.selectionSameAsCurrent) {
          try {
            await this.archiveGroup();
            await this.archiveGroup(); // TODO: why does it break without this?
          } catch (e) {
            // ignore 404s
            if (e.response != null && e.response.status !== 404) {
              throw e;
            }
          }
        }

        if (this.classMode) {
          await this.getMyGroup({ gameId, classId });
        } else {
          await this.getMyGroup({ gameId });
        }

        if (this.group && this.group._id) {
          window.location.href = `/groups/${this.group._id}`;
        } else {
          window.location.href = '/demo';
        }
      } catch (_) {
        // console.log(Object.assign({}, e));
        const message = 'Error restarting gamulation';
        this.flashDanger({ message });
        return;
      }

      if (this.$route.path === '/demo') this.$router.go();
      else this.$router.replace('/demo');
    },
    onCancel() {
      this.$emit('input', {});
    }
  },
  validations: {
    gameId: { required },
    classId: { required: requiredIf(() => this.classMode) }
  }
};
</script>

<style lang="scss" scoped>
.game-launcher {
  z-index: 1000;
  width: 100%;
  // font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
</style>



// WEBPACK FOOTER //
// src/components/ui/modals/GameLauncher.vue